import { Suspense, lazy, useEffect, Component, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useUiTextStore } from '@/stores/uiTextStore';
import MainLayout from '@/layouts/MainLayout';
import ProtectedRoute from '@/components/ui/ProtectedRoute';
import { useTierAccess } from '@/lib/useTierAccess';

/**
 * Lazy import wrapper that auto-reloads on chunk load failure.
 * After a deploy, old chunk hashes no longer exist on the CDN.
 * The server returns HTML instead of JS, causing a MIME type error.
 * This catches that and does a hard reload (once) to get fresh HTML with new chunk refs.
 */
function lazyWithReload(importFn: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    importFn().catch(() => {
      const reloadKey = 'artrouting_chunk_reload';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();
      // Only auto-reload once per 30 seconds to avoid infinite loops
      if (!lastReload || now - parseInt(lastReload) > 30000) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
      }
      // Return a no-op component while reloading
      return { default: () => null } as { default: React.ComponentType<any> };
    })
  );
}

/**
 * Error boundary to catch chunk load failures at render time.
 */
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    if (error.message?.includes('dynamically imported module') || error.message?.includes('Failed to fetch')) {
      const reloadKey = 'artrouting_chunk_reload';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload) > 30000) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
      }
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
          <p>A new version is available.</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1.5rem', background: '#F5A623', color: '#1a1a2e', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auth pages
const LoginPage = lazyWithReload(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazyWithReload(() => import('@/pages/auth/RegisterPage'));
const VerifyEmailPage = lazyWithReload(() => import('@/pages/auth/VerifyEmailPage'));
const WelcomePage = lazyWithReload(() => import('@/pages/onboarding/WelcomePage'));
const AuthorshipPage = lazyWithReload(() => import('@/pages/authorship/AuthorshipPage'));
const LearnPage = lazyWithReload(() => import('@/pages/LearnPage'));
const GalleryEditorPage = lazyWithReload(() => import('@/pages/gallery/GalleryEditorPage'));
const AdminHeroImage = lazyWithReload(() => import('@/pages/admin/AdminHeroImage'));

// Public pages
const HomePage = lazyWithReload(() => import('@/pages/HomePage'));
const MapPage = lazyWithReload(() => import('@/pages/map/MapPage'));
const PinDetailPage = lazyWithReload(() => import('@/pages/map/PinDetailPage'));
const PinEditPage = lazyWithReload(() => import('@/pages/map/PinEditPage'));
const MyPinsPage = lazyWithReload(() => import('@/pages/map/MyPinsPage'));
const DiscoverPage = lazyWithReload(() => import('@/pages/discover/DiscoverPage'));

// Artworks
const ArtworksListPage = lazyWithReload(() => import('@/pages/artworks/ArtworksListPage'));
const ArtworkDetailPage = lazyWithReload(() => import('@/pages/artworks/ArtworkDetailPage'));
const ArtworkCreatePage = lazyWithReload(() => import('@/pages/artworks/ArtworkCreatePage'));
const ArtworkEditPage = lazyWithReload(() => import('@/pages/artworks/ArtworkEditPage'));
const MyArtworksPage = lazyWithReload(() => import('@/pages/artworks/MyArtworksPage'));
const ArtworkQrRedirect = lazyWithReload(() => import('@/pages/artworks/ArtworkQrRedirect'));
const ArtworkStoryPage = lazyWithReload(() => import('@/pages/artworks/ArtworkStoryPage'));
const ArtworkStoryEditPage = lazyWithReload(() => import('@/pages/artworks/ArtworkStoryEditPage'));

// Routes
const RoutesListPage = lazyWithReload(() => import('@/pages/routes/RoutesListPage'));
const RouteDetailPage = lazyWithReload(() => import('@/pages/routes/RouteDetailPage'));
const RouteCreatePage = lazyWithReload(() => import('@/pages/routes/RouteCreatePage'));
const RouteEditPage = lazyWithReload(() => import('@/pages/routes/RouteEditPage'));
const MyRoutesPage = lazyWithReload(() => import('@/pages/routes/MyRoutesPage'));
const MyRoutePlannerPage = lazyWithReload(() => import('@/pages/routes/MyRoutePlannerPage'));

// Events
const EventsListPage = lazyWithReload(() => import('@/pages/events/EventsListPage'));
const MyEventsPage = lazyWithReload(() => import('@/pages/events/MyEventsPage'));
const EventDetailPage = lazyWithReload(() => import('@/pages/events/EventDetailPage'));
const EventCreatePage = lazyWithReload(() => import('@/pages/events/EventCreatePage'));
const EventEditPage = lazyWithReload(() => import('@/pages/events/EventEditPage'));

// Profiles
const ProfilesListPage = lazyWithReload(() => import('@/pages/profiles/ProfilesListPage'));
const ProfileDetailPage = lazyWithReload(() => import('@/pages/profiles/ProfileDetailPage'));
const ProfileCreatePage = lazyWithReload(() => import('@/pages/profiles/ProfileCreatePage'));
const ProfileEditPage = lazyWithReload(() => import('@/pages/profiles/ProfileEditPage'));
const ProfileCompletePage = lazyWithReload(() => import('@/pages/profiles/ProfileCompletePage'));
const ProfileImportPage = lazyWithReload(() => import('@/pages/profiles/import/ProfileImportPage'));
const MyProfilePage = lazyWithReload(() => import('@/pages/profiles/MyProfilePage'));

// Curators (Explorer Pro)
const MyArtistsPage = lazyWithReload(() => import('@/pages/curators/MyArtistsPage'));
const MyArtistDetailPage = lazyWithReload(() => import('@/pages/curators/MyArtistDetailPage'));
const MyCuratorsPage = lazyWithReload(() => import('@/pages/curators/MyCuratorsPage'));

// ArtRadar
const ArtRadarNewspaperPage = lazyWithReload(() => import('@/pages/artradar/ArtRadarNewspaperPage'));
const ArticlesListPage = lazyWithReload(() => import('@/pages/artradar/ArticlesListPage'));
const ArticleDetailPage = lazyWithReload(() => import('@/pages/artradar/ArticleDetailPage'));
const ArticleEditPage = lazyWithReload(() => import('@/pages/artradar/ArticleEditPage'));
const MyArticlesPage = lazyWithReload(() => import('@/pages/artradar/MyArticlesPage'));
const SpecialEditionPage = lazyWithReload(() => import('@/pages/artradar/SpecialEditionPage'));
const EditionsListPage = lazyWithReload(() => import('@/pages/artradar/EditionsListPage'));
const ArticleSubmitPage = lazyWithReload(() => import('@/pages/artradar/ArticleSubmitPage'));
const ArtRadarAboutPage = lazyWithReload(() => import('@/pages/artradar/ArtRadarAboutPage'));
const ContributorPage = lazyWithReload(() => import('@/pages/artradar/ContributorPage'));
const AdminArtRadarReview = lazyWithReload(() => import('@/pages/admin/AdminArtRadarReview'));
const AdminCollections = lazyWithReload(() => import('@/pages/admin/AdminCollections'));

// Bookmarks
const InterestsPage = lazyWithReload(() => import('@/pages/interests/InterestsPage'));
const InsightsPage = lazyWithReload(() => import('@/pages/insights/InsightsPage'));
const InterestsUnsubscribePage = lazyWithReload(() => import('@/pages/interests/InterestsUnsubscribePage'));

// Developer API
const ApiKeysPage = lazyWithReload(() => import('@/pages/developer/ApiKeysPage'));
const ApiExplorerPage = lazyWithReload(() => import('@/pages/developer/ApiExplorerPage'));

// Provenance
const ProvenanceViewPage = lazyWithReload(() => import('@/pages/provenance/ProvenanceViewPage'));
const ProvenanceClaimPage = lazyWithReload(() => import('@/pages/provenance/ProvenanceClaimPage'));
const MyProvenancePage = lazyWithReload(() => import('@/pages/provenance/MyProvenancePage'));
const ProvenanceQrPage = lazyWithReload(() => import('@/pages/provenance/ProvenanceQrPage'));

// Lens (discovery cards)
const LensCardPage = lazyWithReload(() => import('@/pages/lens/LensCardPage'));
const LensQrPrintPage = lazyWithReload(() => import('@/pages/lens/LensQrPrintPage'));

// Submissions
const SubmissionsListPage = lazyWithReload(() => import('@/pages/submissions/SubmissionsListPage'));
const SubmissionCreatePage = lazyWithReload(() => import('@/pages/submissions/SubmissionCreatePage'));
const SubmissionDetailPage = lazyWithReload(() => import('@/pages/submissions/SubmissionDetailPage'));

// Docs
const DocsPage = lazyWithReload(() => import('@/pages/docs/DocsPage'));
const DocEditPage = lazyWithReload(() => import('@/pages/docs/DocEditPage'));

// Contact
const ContactPage = lazyWithReload(() => import('@/pages/contact/ContactPage'));
const QaPage = lazyWithReload(() => import('@/pages/QaPage'));

// Legal
const PrivacyPage = lazyWithReload(() => import('@/pages/legal/PrivacyPage'));
const TermsPage = lazyWithReload(() => import('@/pages/legal/TermsPage'));
const CookiesPage = lazyWithReload(() => import('@/pages/legal/CookiesPage'));
const ImpressumPage = lazyWithReload(() => import('@/pages/legal/ImpressumPage'));
const AuthorshipRulesPage = lazyWithReload(() => import('@/pages/legal/AuthorshipRulesPage'));
const MessagingRulesPage = lazyWithReload(() => import('@/pages/legal/MessagingRulesPage'));

// Consignments
const MyConsignmentsPage = lazyWithReload(() => import('@/pages/consignments/MyConsignmentsPage'));
const ConsignmentInboxPage = lazyWithReload(() => import('@/pages/consignments/ConsignmentInboxPage'));

// Pricing
const PricingPage = lazyWithReload(() => import('@/pages/PricingPage'));

// Subscription
const SubscriptionPage = lazyWithReload(() => import('@/pages/SubscriptionPage'));

// Profile Wizard
const ProfileWizard = lazyWithReload(() => import('@/pages/profiles/ProfileWizard'));

// Account
const AccountSettingsPage = lazyWithReload(() => import('@/pages/AccountSettingsPage'));

// Admin
const AdminDashboard = lazyWithReload(() => import('@/pages/admin/AdminDashboard'));
const AdminOnboardingEmails = lazyWithReload(() => import('@/pages/admin/AdminOnboardingEmails'));
const AdminAgentQueue = lazyWithReload(() => import('@/pages/admin/AdminAgentQueue'));
const AdminUsers = lazyWithReload(() => import('@/pages/admin/AdminUsers'));
const AdminFreeProReport = lazyWithReload(() => import('@/pages/admin/AdminFreeProReport'));
const AdminContent = lazyWithReload(() => import('@/pages/admin/AdminContent'));
const AdminAudit = lazyWithReload(() => import('@/pages/admin/AdminAudit'));
const AdminConfig = lazyWithReload(() => import('@/pages/admin/AdminConfig'));
const AdminClaims = lazyWithReload(() => import('@/pages/admin/AdminClaims'));
const AdminMessagingConfig = lazyWithReload(() => import('@/pages/admin/AdminMessagingConfig'));
const MessagesPage = lazyWithReload(() => import('@/pages/messaging/MessagesPage'));
const AdminSubmissions = lazyWithReload(() => import('@/pages/admin/AdminSubmissions'));
const AdminApiSettings = lazyWithReload(() => import('@/pages/admin/AdminApiSettings'));
const DiscoveryPage = lazyWithReload(() => import('@/pages/admin/DiscoveryPage'));
const TierEditorPage = lazyWithReload(() => import('@/pages/admin/TierEditorPage'));
const AdminTierNames = lazyWithReload(() => import('@/pages/admin/AdminTierNames'));
const AdminTierPricing = lazyWithReload(() => import('@/pages/admin/AdminTierPricing'));
const AdminSubscriptions = lazyWithReload(() => import('@/pages/admin/AdminSubscriptions'));
const AdminPayPalSettings = lazyWithReload(() => import('@/pages/admin/AdminPayPalSettings'));
const AdminSmtpSettings = lazyWithReload(() => import('@/pages/admin/AdminSmtpSettings'));
const AdminLandingEditor = lazyWithReload(() => import('@/pages/admin/AdminLandingEditor'));
const AdminMarketingQR = lazyWithReload(() => import('@/pages/admin/AdminMarketingQR'));
const AdminTranslations = lazyWithReload(() => import('@/pages/admin/AdminTranslations'));
const AdminGeocodeEnrich = lazyWithReload(() => import('@/pages/admin/AdminGeocodeEnrich'));

/**
 * TierRoute — wraps a protected route with tier-based menu access enforcement.
 * If the user's tier doesn't have access to the menuKey, redirects to /pricing.
 */
function TierRoute({ menuKey, children }: { menuKey: string; children: React.ReactNode }) {
  const { allowed } = useTierAccess(menuKey);
  if (!allowed) {
    return <Navigate to="/pricing" replace />;
  }
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

const SuspenseFallback = (
  <div className="placeholder-page">
    <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px' }}></i>
  </div>
);

export default function App() {
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const loadUiText = useUiTextStore((s) => s.load);

  useEffect(() => {
    checkAuth();
    // Superadmin text edits apply for everyone, so every visitor loads them.
    loadUiText();
  }, [checkAuth, loadUiText]);

  return (
    <ChunkErrorBoundary>
    <Suspense fallback={SuspenseFallback}>
      <Routes>
        {/* All routes use MainLayout */}
        <Route element={<MainLayout />}>
          {/* Auth routes */}
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
          <Route path="/auth/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
          <Route path="/authorship" element={<ProtectedRoute><AuthorshipPage /></ProtectedRoute>} />
          <Route path="/authorship/:id" element={<ProtectedRoute><AuthorshipPage /></ProtectedRoute>} />
          <Route path="/learn/:topic" element={<LearnPage />} />
          <Route path="/gallery/:ownerType/:ownerId/edit" element={<ProtectedRoute><GalleryEditorPage /></ProtectedRoute>} />
          <Route path="/admin/hero-image" element={<ProtectedRoute><AdminHeroImage /></ProtectedRoute>} />
          <Route path="/admin/artradar-review" element={<ProtectedRoute><AdminArtRadarReview /></ProtectedRoute>} />

          {/* Public */}
          <Route path="/" element={<HomePage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/map/pin/new" element={<ProtectedRoute><PinEditPage /></ProtectedRoute>} />
          <Route path="/map/pin/:id" element={<PinDetailPage />} />
          <Route path="/map/my-pins" element={<ProtectedRoute><MyPinsPage /></ProtectedRoute>} />
          <Route path="/map/pin/create" element={<ProtectedRoute><PinEditPage /></ProtectedRoute>} />
          <Route path="/map/pin/:id/edit" element={<ProtectedRoute><PinEditPage /></ProtectedRoute>} />
          <Route path="/discover" element={<DiscoverPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />

          {/* Artworks */}
          <Route path="/artworks" element={<ArtworksListPage />} />
          <Route path="/artworks/my" element={<TierRoute menuKey="my_artworks"><MyArtworksPage /></TierRoute>} />
          <Route path="/artworks/new" element={<TierRoute menuKey="artworks_create"><ArtworkCreatePage /></TierRoute>} />
          <Route path="/artworks/create" element={<TierRoute menuKey="artworks_create"><ArtworkCreatePage /></TierRoute>} />
          <Route path="/artworks/:id/qr" element={<ArtworkQrRedirect />} />
          <Route path="/artworks/:id" element={<ArtworkDetailPage />} />
          <Route path="/artworks/:id/edit" element={<ProtectedRoute><ArtworkEditPage /></ProtectedRoute>} />
          {/* Artwork Story — public read/listen page + owner editor */}
          <Route path="/story/:uid" element={<ArtworkStoryPage />} />
          <Route path="/artworks/:id/story/edit" element={<ProtectedRoute><ArtworkStoryEditPage /></ProtectedRoute>} />

          {/* Routes */}
          <Route path="/routes" element={<RoutesListPage />} />
          {/* Explorer route planner — a visitor's own stops, no account needed */}
          <Route path="/my-route" element={<MyRoutePlannerPage />} />
          <Route path="/routes/my" element={<TierRoute menuKey="my_routes"><MyRoutesPage /></TierRoute>} />
          <Route path="/routes/create" element={<TierRoute menuKey="routes_create"><RouteCreatePage /></TierRoute>} />
          <Route path="/routes/new" element={<TierRoute menuKey="routes_create"><RouteCreatePage /></TierRoute>} />
          <Route path="/routes/:id" element={<RouteDetailPage />} />
          <Route path="/routes/:id/edit" element={<ProtectedRoute><RouteEditPage /></ProtectedRoute>} />

          {/* ArtLens was retired: artwork QR codes open in the phone's own camera. Old links land home. */}
          <Route path="/artlens" element={<Navigate to="/" replace />} />

          {/* Events */}
          <Route path="/events" element={<EventsListPage />} />
          <Route path="/events/my" element={<ProtectedRoute><MyEventsPage /></ProtectedRoute>} />
          <Route path="/events/new" element={<TierRoute menuKey="events_create"><EventCreatePage /></TierRoute>} />
          <Route path="/events/create" element={<TierRoute menuKey="events_create"><EventCreatePage /></TierRoute>} />
          <Route path="/events/:id" element={<EventDetailPage />} />
          <Route path="/events/:id/edit" element={<ProtectedRoute><EventEditPage /></ProtectedRoute>} />

          {/* Profiles */}
          <Route path="/profiles" element={<ProfilesListPage />} />
          <Route path="/profiles/new" element={<ProtectedRoute><ProfileCreatePage /></ProtectedRoute>} />
          <Route path="/profiles/create" element={<ProtectedRoute><ProfileCreatePage /></ProtectedRoute>} />
          <Route path="/profiles/my" element={<ProtectedRoute><MyProfilePage /></ProtectedRoute>} />
          <Route path="/profiles/import" element={<ProtectedRoute><ProfileImportPage /></ProtectedRoute>} />
          <Route path="/profiles/:id" element={<ProfileDetailPage />} />
          <Route path="/profiles/wizard" element={<ProtectedRoute><ProfileWizard /></ProtectedRoute>} />
          <Route path="/profiles/:id/edit" element={<ProtectedRoute><ProfileEditPage /></ProtectedRoute>} />
          <Route path="/profiles/:id/complete" element={<ProtectedRoute><ProfileCompletePage /></ProtectedRoute>} />

          {/* Account Settings */}
          <Route path="/account/settings" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />

          {/* Curators */}
          <Route path="/my-artists" element={<ProtectedRoute><MyArtistsPage /></ProtectedRoute>} />
          <Route path="/my-artists/:profileId" element={<ProtectedRoute><MyArtistDetailPage /></ProtectedRoute>} />
          <Route path="/my-curators" element={<ProtectedRoute><MyCuratorsPage /></ProtectedRoute>} />

          {/* ArtRadar */}
          <Route path="/artradar" element={<ArtRadarNewspaperPage />} />
          <Route path="/artradar/list" element={<ArticlesListPage />} />
          <Route path="/artradar/editions" element={<EditionsListPage />} />
          <Route path="/artradar/edition/:slug" element={<SpecialEditionPage />} />
          <Route path="/artradar/my" element={<ProtectedRoute><MyArticlesPage /></ProtectedRoute>} />
          <Route path="/artradar/about" element={<ArtRadarAboutPage />} />
          <Route path="/artradar/submit" element={<ProtectedRoute><ArticleSubmitPage /></ProtectedRoute>} />
          <Route path="/artradar/submit/:id" element={<ProtectedRoute><ArticleSubmitPage /></ProtectedRoute>} />
          <Route path="/artradar/contributor" element={<ProtectedRoute><ContributorPage /></ProtectedRoute>} />
          <Route path="/artradar/edit/:id" element={<ProtectedRoute><ArticleEditPage /></ProtectedRoute>} />
          <Route path="/artradar/article/:slug" element={<ArticleDetailPage />} />
          <Route path="/artradar/article/:slug/edit" element={<ProtectedRoute><ArticleEditPage /></ProtectedRoute>} />
          <Route path="/artradar/:slug" element={<ArticleDetailPage />} />
          <Route path="/artradar/:slug/edit" element={<ProtectedRoute><ArticleEditPage /></ProtectedRoute>} />
          <Route path="/admin/collections" element={<ProtectedRoute><AdminCollections /></ProtectedRoute>} />
          <Route path="/admin/collections/:id" element={<ProtectedRoute><AdminCollections /></ProtectedRoute>} />

          {/* Bookmarks */}
          {/* ♡ Interests — Saved (bookmarks) lives inside it now */}
          <Route path="/interests" element={<ProtectedRoute><InterestsPage /></ProtectedRoute>} />
          <Route path="/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
          <Route path="/interests/unsubscribe/:token" element={<InterestsUnsubscribePage />} />
          <Route path="/bookmarks" element={<Navigate to="/interests" replace />} />

          {/* Developer API — keys and the testable API explorer */}
          <Route path="/developer/api-keys" element={<ProtectedRoute><ApiKeysPage /></ProtectedRoute>} />
          <Route path="/developer/api" element={<ProtectedRoute><ApiExplorerPage /></ProtectedRoute>} />

          {/* Consignments */}
          <Route path="/consignments/my" element={<ProtectedRoute><MyConsignmentsPage /></ProtectedRoute>} />
          <Route path="/consignments/inbox" element={<ProtectedRoute><ConsignmentInboxPage /></ProtectedRoute>} />

          {/* Lens (discovery cards) */}
          <Route path="/a/:entityType/:slug/qr" element={<LensQrPrintPage />} />
          <Route path="/a/:entityType/:slug" element={<LensCardPage />} />

          {/* Provenance */}
          <Route path="/p/:uid" element={<ProvenanceViewPage />} />
          <Route path="/p/:uid/qr" element={<ProvenanceQrPage />} />
          <Route path="/provenance/my" element={<ProtectedRoute><MyProvenancePage /></ProtectedRoute>} />
          <Route path="/provenance/claim/:uid" element={<ProtectedRoute><ProvenanceClaimPage /></ProtectedRoute>} />

          {/* Submissions */}
          <Route path="/submissions" element={<ProtectedRoute><SubmissionsListPage /></ProtectedRoute>} />
          <Route path="/submissions/create" element={<ProtectedRoute><SubmissionCreatePage /></ProtectedRoute>} />
          <Route path="/submissions/:id" element={<ProtectedRoute><SubmissionDetailPage /></ProtectedRoute>} />

          {/* Docs */}
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/:filename" element={<DocsPage />} />
          <Route path="/docs/:filename/edit" element={<ProtectedRoute><DocEditPage /></ProtectedRoute>} />

          {/* Contact */}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about/qa" element={<QaPage />} />

          {/* Legal */}
          <Route path="/legal/privacy" element={<PrivacyPage />} />
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="/legal/cookies" element={<CookiesPage />} />
          <Route path="/legal/impressum" element={<ImpressumPage />} />
          <Route path="/legal/authorship" element={<AuthorshipRulesPage />} />
          <Route path="/legal/messaging" element={<MessagingRulesPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/freepro-report" element={<ProtectedRoute><AdminFreeProReport /></ProtectedRoute>} />
          <Route path="/admin/onboarding-emails" element={<ProtectedRoute><AdminOnboardingEmails /></ProtectedRoute>} />
          <Route path="/admin/agent-queue" element={<ProtectedRoute><AdminAgentQueue /></ProtectedRoute>} />
          <Route path="/admin/content" element={<ProtectedRoute><AdminContent /></ProtectedRoute>} />
          <Route path="/admin/audit" element={<ProtectedRoute><AdminAudit /></ProtectedRoute>} />
          <Route path="/admin/config" element={<ProtectedRoute><AdminConfig /></ProtectedRoute>} />
          <Route path="/admin/claims" element={<ProtectedRoute><AdminClaims /></ProtectedRoute>} />
          <Route path="/admin/messaging" element={<ProtectedRoute><AdminMessagingConfig /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
          <Route path="/messages/:id" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
          <Route path="/admin/submissions" element={<ProtectedRoute><AdminSubmissions /></ProtectedRoute>} />
          <Route path="/admin/api-settings" element={<ProtectedRoute><AdminApiSettings /></ProtectedRoute>} />
          <Route path="/admin/discovery" element={<ProtectedRoute><DiscoveryPage /></ProtectedRoute>} />
          <Route path="/admin/tier-editor" element={<ProtectedRoute><TierEditorPage /></ProtectedRoute>} />
          <Route path="/admin/tier-names" element={<ProtectedRoute><AdminTierNames /></ProtectedRoute>} />
          <Route path="/admin/tier-pricing" element={<ProtectedRoute><AdminTierPricing /></ProtectedRoute>} />
          <Route path="/admin/subscriptions" element={<ProtectedRoute><AdminSubscriptions /></ProtectedRoute>} />
          <Route path="/admin/paypal" element={<ProtectedRoute><AdminPayPalSettings /></ProtectedRoute>} />
          <Route path="/admin/smtp" element={<ProtectedRoute><AdminSmtpSettings /></ProtectedRoute>} />
          <Route path="/admin/landing" element={<ProtectedRoute><AdminLandingEditor /></ProtectedRoute>} />
          <Route path="/admin/marketing-qr" element={<ProtectedRoute><AdminMarketingQR /></ProtectedRoute>} />
          <Route path="/admin/translations" element={<ProtectedRoute><AdminTranslations /></ProtectedRoute>} />
          <Route path="/admin/geocode-enrich" element={<ProtectedRoute><AdminGeocodeEnrich /></ProtectedRoute>} />
        </Route>
      </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
}
